import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import { currentLanguage, pageId, languages } from '@/i18n'
import { BASE_URL, DEFAULT_LANGUAGE, getCanonicalUrl } from '@/i18n/config'

function getSpokeCanonicalUrl(langCode: string, spokeSlug: string): string {
  const lang = languages[langCode]
  const langPath = lang.path || `/${lang.code}`
  return `${BASE_URL}${langPath}/${spokeSlug}/`
}

export function usePageHead() {
  const lang = computed(() => languages[currentLanguage.value])
  const isSpoke = computed(() => pageId.value !== 'hub')

  const seo = computed(() => {
    if (isSpoke.value) {
      const spoke = lang.value.spokes?.[pageId.value]
      return spoke ? spoke.seo : lang.value.seo
    }
    return lang.value.seo
  })

  const canonicalUrl = computed(() => {
    if (isSpoke.value) {
      return getSpokeCanonicalUrl(currentLanguage.value, pageId.value)
    }
    return getCanonicalUrl(currentLanguage.value)
  })

  const webManifest = computed(() => {
    return lang.value.path ? `/site-${lang.value.code}.webmanifest` : '/site.webmanifest'
  })

  // Hreflang links
  const hreflangLinks = computed(() => {
    const links: Array<{ rel: string; hreflang: string; href: string }> = []

    if (isSpoke.value) {
      // Spoke: link to same spoke in all languages that have it
      for (const l of Object.values(languages)) {
        if (l.spokes?.[pageId.value]) {
          links.push({
            rel: 'alternate',
            hreflang: l.code,
            href: getSpokeCanonicalUrl(l.code, pageId.value)
          })
        }
      }
      const defaultLang = languages[DEFAULT_LANGUAGE]
      if (defaultLang?.spokes?.[pageId.value]) {
        links.push({
          rel: 'alternate',
          hreflang: 'x-default',
          href: getSpokeCanonicalUrl(DEFAULT_LANGUAGE, pageId.value)
        })
      }
    } else {
      // Hub: link to all language hubs
      for (const l of Object.values(languages)) {
        links.push({
          rel: 'alternate',
          hreflang: l.code,
          href: getCanonicalUrl(l.code)
        })
      }
      links.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: getCanonicalUrl(DEFAULT_LANGUAGE)
      })
    }

    return links
  })

  // JSON-LD: WebApplication
  const webAppSchema = computed(() => {
    const hubSeo = lang.value.seo
    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: seo.value.schemaName,
      description: seo.value.schemaDescription,
      url: canonicalUrl.value,
      applicationCategory: 'MultimediaApplication',
      operatingSystem: 'Web Browser',
      browserRequirements: 'Modern web browser with JavaScript enabled',
      featureList: seo.value.schemaFeatureList,
      fileFormat: ['MP3', 'WAV', 'OGG', 'AAC', 'FLAC', 'M4A'],
      inLanguage: lang.value.code,
      author: { '@type': 'Organization', name: 'Audio Cutter' },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: hubSeo.schemaCurrency
      }
    }

    // Only hub pages from the hub seo have alternateNames
    if (!isSpoke.value && hubSeo.schemaAlternateNames) {
      schema.alternateName = hubSeo.schemaAlternateNames
    }

    return schema
  })

  // FAQ items: spoke pages override the hub FAQ
  const faqItems = computed(() => {
    const l = lang.value
    return l.spokes?.[pageId.value]?.ui.faqItems ?? l.ui.faqItems
  })

  // JSON-LD: FAQPage (matches the visible FAQ section on every page)
  const faqSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.value.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer }
    }))
  }))

  // JSON-LD: HowTo
  const howToSchema = computed(() => ({
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: seo.value.schemaHowToName,
    description: seo.value.schemaHowToDescription,
    totalTime: 'PT2M',
    tool: { '@type': 'HowToTool', name: 'Web Browser' },
    step: seo.value.schemaHowToSteps.map((step, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: step.name,
      text: step.text
    }))
  }))

  // JSON-LD: BreadcrumbList
  const breadcrumbSchema = computed(() => {
    const hubSeo = lang.value.seo
    const items: Array<Record<string, unknown>> = [
      {
        '@type': 'ListItem',
        position: 1,
        name: hubSeo.schemaBreadcrumbHome,
        item: `${BASE_URL}/`
      }
    ]

    if (isSpoke.value) {
      // 3-level: Home > Hub > Spoke
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: hubSeo.schemaName,
        item: getCanonicalUrl(currentLanguage.value)
      })
      items.push({
        '@type': 'ListItem',
        position: 3,
        name: seo.value.schemaName,
        item: canonicalUrl.value
      })
    } else {
      // 2-level: Home > Hub
      items.push({
        '@type': 'ListItem',
        position: 2,
        name: seo.value.schemaName,
        item: canonicalUrl.value
      })
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items
    }
  })

  // OG fields: hub seo for ogSiteName/ogLocale, spoke seo for title/description
  const hubSeo = computed(() => lang.value.seo)

  useHead({
    htmlAttrs: {
      lang: () => lang.value.code,
      dir: () => lang.value.dir,
      'data-page-id': () => pageId.value
    },
    title: () => seo.value.title,
    meta: [
      { charset: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'description', content: () => seo.value.description },
      { name: 'theme-color', content: '#f59e0b' },
      { name: 'google-site-verification', content: 'oWQlnTXyghckBJV2luVW_CwKKEpTqXm_ls5Nd7RcPf4' },
      // Open Graph
      { property: 'og:type', content: 'website' },
      { property: 'og:title', content: () => seo.value.ogTitle },
      { property: 'og:description', content: () => seo.value.ogDescription },
      { property: 'og:url', content: () => canonicalUrl.value },
      { property: 'og:site_name', content: () => hubSeo.value.ogSiteName },
      { property: 'og:image', content: `${BASE_URL}/og-image.jpg` },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: () => seo.value.ogTitle },
      { property: 'og:locale', content: () => hubSeo.value.ogLocale },
      // Twitter
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: () => seo.value.twitterTitle },
      { name: 'twitter:description', content: () => seo.value.twitterDescription },
      { name: 'twitter:image', content: `${BASE_URL}/og-image.jpg` },
      // PWA
      { name: 'apple-mobile-web-app-title', content: () => hubSeo.value.appleWebAppTitle }
    ],
    link: () => [
      // Canonical
      { rel: 'canonical', href: canonicalUrl.value },
      // Favicon
      { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
      { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
      { rel: 'manifest', href: webManifest.value },
      // Hreflang
      ...hreflangLinks.value
    ],
    script: [
      // JSON-LD schemas
      { type: 'application/ld+json', innerHTML: () => JSON.stringify(webAppSchema.value) },
      { type: 'application/ld+json', innerHTML: () => JSON.stringify(howToSchema.value) },
      { type: 'application/ld+json', innerHTML: () => JSON.stringify(breadcrumbSchema.value) },
      { type: 'application/ld+json', innerHTML: () => JSON.stringify(faqSchema.value) }
    ]
  })
}
